import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Play, Pause, RotateCcw, FastForward, Clock, Target, Shield, AlertTriangle,
  ChevronDown, ChevronUp, Terminal, Wifi, Database, Lock, Bug, Zap,
  Server, Activity, Eye, CheckCircle, XCircle, Search, Filter, SkipForward,
  Monitor, Globe, ArrowRight, Crosshair
} from 'lucide-react';

// Phase icons and colors mapping
const PHASE_CONFIG = {
  reconnaissance: {
    icon: Search,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/50',
    label: 'Reconnaissance'
  },
  enumeration: {
    icon: Eye,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-500/50',
    label: 'Énumération'
  },
  vulnerability: {
    icon: Bug,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-500/50',
    label: 'Vulnerability Scan'
  },
  exploitation: {
    icon: Zap,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/50',
    label: 'Exploitation'
  },
  post_exploitation: {
    icon: Lock,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/50',
    label: 'Post-Exploitation'
  }
};

// Severity colors
const SEVERITY_COLORS = {
  critical: 'text-red-500 bg-red-500/20 border-red-500',
  high: 'text-orange-500 bg-orange-500/20 border-orange-500',
  medium: 'text-yellow-500 bg-yellow-500/20 border-yellow-500',
  low: 'text-blue-500 bg-blue-500/20 border-blue-500',
  info: 'text-gray-400 bg-gray-500/20 border-gray-500'
};

// Tool icons
const TOOL_ICONS = {
  nmap: Terminal,
  openvas: Shield,
  nuclei: Bug,
  zap: AlertTriangle,
  metasploit: Zap,
  nikto: Search,
  sqlmap: Database,
  gobuster: Search
};

// Attack Path Visualization Component (n8n-style)
const AttackPathVisualization = ({ scanId, vulnerabilities = [], hosts = [] }) => {
  // Group vulnerabilities by severity and exploitation status
  const attackData = useMemo(() => {
    const criticalVulns = vulnerabilities.filter(v => v.severity === 'critical');
    const highVulns = vulnerabilities.filter(v => v.severity === 'high');
    const exploitedVulns = vulnerabilities.filter(v => v.exploitation_successful);
    const uniqueHosts = [...new Set(vulnerabilities.map(v => v.host_ip || v.ip_address).filter(Boolean))];
    const uniqueCVEs = [...new Set(vulnerabilities.map(v => v.cve_id).filter(Boolean))];

    return {
      totalVulns: vulnerabilities.length,
      criticalCount: criticalVulns.length,
      highCount: highVulns.length,
      exploitedCount: exploitedVulns.length,
      exploitedVulns,
      criticalVulns,
      highVulns,
      hosts: uniqueHosts,
      cves: uniqueCVEs
    };
  }, [vulnerabilities]);

  // Node component for the workflow
  const WorkflowNode = ({ type, icon: Icon, title, count, items, color, glowColor, isActive, onClick }) => (
    <div
      className={`relative group cursor-pointer transition-all duration-300 ${isActive ? 'scale-105' : 'hover:scale-102'}`}
      onClick={onClick}
    >
      {/* Glow effect */}
      {isActive && (
        <div className={`absolute inset-0 ${glowColor} blur-xl opacity-50 rounded-2xl`}></div>
      )}

      <div className={`relative bg-brand-black border-2 ${color} rounded-2xl p-4 min-w-[160px] flex-1 max-w-[200px]
                      shadow-lg hover:shadow-xl transition-all duration-300
                      ${isActive ? 'ring-2 ring-offset-2 ring-offset-brand-dark' : ''}`}
           style={{ boxShadow: isActive ? `0 0 30px ${glowColor}` : '' }}>
        {/* Node header */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg ${color.replace('border-', 'bg-').replace('/50', '/20')}
                          flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${color.replace('border-', 'text-').replace('/50', '')}`} />
          </div>
          <span className="text-xs text-gray-400 uppercase tracking-wide">{type}</span>
        </div>

        {/* Node content */}
        <div className="text-white font-bold text-lg">{title}</div>
        {count !== undefined && (
          <div className={`text-2xl font-bold mt-1 ${color.replace('border-', 'text-').replace('/50', '')}`}>
            {count}
          </div>
        )}

        {/* Items preview */}
        {items && items.length > 0 && (
          <div className="mt-2 space-y-1 max-h-20 overflow-hidden">
            {items.slice(0, 3).map((item, idx) => (
              <div key={idx} className="text-xs text-gray-400 truncate">{item}</div>
            ))}
            {items.length > 3 && (
              <div className="text-xs text-gray-500">+{items.length - 3} more...</div>
            )}
          </div>
        )}

        {/* Connection dot (right) */}
        <div className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 rounded-full
                        ${color.replace('border-', 'bg-').replace('/50', '')} border-2 border-brand-dark`}></div>

        {/* Connection dot (left) */}
        <div className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full
                        ${color.replace('border-', 'bg-').replace('/50', '')} border-2 border-brand-dark`}></div>
      </div>
    </div>
  );

  // Connection line component
  const ConnectionLine = ({ animated, success }) => (
    <div className="flex items-center mx-2">
      <div className={`h-0.5 w-8 ${success ? 'bg-green-500' : 'bg-gray-600'} relative overflow-hidden`}>
        {animated && (
          <div className={`absolute inset-0 ${success ? 'bg-green-400' : 'bg-brand-green'}`}
               style={{
                 animation: 'flowRight 1s linear infinite',
               }}>
          </div>
        )}
      </div>
      <ArrowRight className={`w-4 h-4 ${success ? 'text-green-500' : 'text-gray-600'}`} />
    </div>
  );

  if (vulnerabilities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Aucune donnée disponible pour la visualisation</p>
        <p className="text-sm mt-1">Les données apparaîtront après le scan</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* CSS for animation */}
      <style>{`
        @keyframes flowRight {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>

      {/* Title */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
          <Crosshair className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Chemin d'Attaque</h3>
          <p className="text-xs text-gray-400">Visualisation du parcours de l'attaque</p>
        </div>
      </div>

      {/* Attack Path Workflow */}
      <div className="bg-brand-black/50 rounded-xl p-6 border border-gray-700 overflow-x-auto">
        <div className="flex items-center justify-center w-full gap-2 flex-wrap lg:flex-nowrap">

          {/* Node 1: Scan Target */}
          <WorkflowNode
            type="Entrée"
            icon={Globe}
            title="Cibles"
            count={attackData.hosts.length}
            items={attackData.hosts}
            color="border-blue-500/50"
            glowColor="rgba(59, 130, 246, 0.3)"
            isActive={true}
          />

          <ConnectionLine animated={true} />

          {/* Node 2: CVE Detection */}
          <WorkflowNode
            type="Détection"
            icon={Bug}
            title="CVE Trouvées"
            count={attackData.cves.length}
            items={attackData.cves}
            color="border-orange-500/50"
            glowColor="rgba(249, 115, 22, 0.3)"
            isActive={attackData.cves.length > 0}
          />

          <ConnectionLine animated={attackData.criticalCount > 0} />

          {/* Node 3: Critical Vulnerabilities */}
          <WorkflowNode
            type="Critique"
            icon={AlertTriangle}
            title="Vulnérabilités"
            count={attackData.criticalCount + attackData.highCount}
            items={[
              `${attackData.criticalCount} critiques`,
              `${attackData.highCount} hautes`
            ]}
            color="border-red-500/50"
            glowColor="rgba(239, 68, 68, 0.3)"
            isActive={attackData.criticalCount > 0}
          />

          <ConnectionLine animated={attackData.exploitedCount > 0} success={attackData.exploitedCount > 0} />

          {/* Node 4: Exploitation */}
          <WorkflowNode
            type="Exploitation"
            icon={Zap}
            title="Exploitées"
            count={attackData.exploitedCount}
            items={attackData.exploitedVulns.map(v => v.cve_id || v.name)}
            color={attackData.exploitedCount > 0 ? "border-purple-500/50" : "border-gray-600/50"}
            glowColor="rgba(168, 85, 247, 0.3)"
            isActive={attackData.exploitedCount > 0}
          />

          <ConnectionLine animated={attackData.exploitedCount > 0} success={attackData.exploitedCount > 0} />

          {/* Node 5: Compromised Targets */}
          <WorkflowNode
            type="Résultat"
            icon={attackData.exploitedCount > 0 ? CheckCircle : Shield}
            title={attackData.exploitedCount > 0 ? "Compromis" : "Protégé"}
            count={attackData.exploitedCount > 0 ? attackData.hosts.length : 0}
            items={attackData.exploitedCount > 0 ? attackData.hosts : ["Aucun système compromis"]}
            color={attackData.exploitedCount > 0 ? "border-green-500/50" : "border-gray-600/50"}
            glowColor="rgba(34, 197, 94, 0.3)"
            isActive={attackData.exploitedCount > 0}
          />
        </div>
      </div>

      {/* Attack Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
          <Monitor className="w-5 h-5 text-blue-400 mx-auto mb-1" />
          <div className="text-xl font-bold text-blue-400">{attackData.hosts.length}</div>
          <div className="text-xs text-gray-400">Hôtes ciblés</div>
        </div>
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-center">
          <Bug className="w-5 h-5 text-orange-400 mx-auto mb-1" />
          <div className="text-xl font-bold text-orange-400">{attackData.cves.length}</div>
          <div className="text-xs text-gray-400">CVE uniques</div>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center">
          <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-1" />
          <div className="text-xl font-bold text-red-400">{attackData.criticalCount}</div>
          <div className="text-xs text-gray-400">Critiques</div>
        </div>
        <div className={`${attackData.exploitedCount > 0 ? 'bg-purple-500/10 border-purple-500/30' : 'bg-gray-500/10 border-gray-600/30'} border rounded-lg p-3 text-center`}>
          <Zap className={`w-5 h-5 mx-auto mb-1 ${attackData.exploitedCount > 0 ? 'text-purple-400' : 'text-gray-500'}`} />
          <div className={`text-xl font-bold ${attackData.exploitedCount > 0 ? 'text-purple-400' : 'text-gray-500'}`}>{attackData.exploitedCount}</div>
          <div className="text-xs text-gray-400">Exploitées</div>
        </div>
      </div>

      {/* Exploited Vulnerabilities Details */}
      {attackData.exploitedCount > 0 && (
        <div className="mt-4 bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-semibold text-purple-300">Exploitations Confirmées</span>
          </div>
          <div className="space-y-2">
            {attackData.exploitedVulns.map((vuln, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-purple-900/30 rounded-lg px-3 py-2">
                <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500/30 text-red-300">
                  {vuln.severity?.toUpperCase()}
                </span>
                <span className="text-purple-200 font-medium">{vuln.cve_id || vuln.name || vuln.title}</span>
                <ArrowRight className="w-3 h-3 text-purple-400" />
                <span className="text-purple-300/60 text-sm">{vuln.host_ip || vuln.ip_address}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AttackTimeline = ({ scanId, vulnerabilities = [], hosts = [] }) => {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Replay state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState(-1);
  const [speed, setSpeed] = useState(1.0);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Filter state
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');

  const eventSourceRef = useRef(null);
  const timelineRef = useRef(null);

  // Fetch timeline data
  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_API_URL || '';

        const response = await fetch(`${backendUrl}/api/v1/timeline/${scanId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });

        if (!response.ok) {
          throw new Error('Failed to fetch timeline');
        }

        const data = await response.json();
        setTimeline(data);

        // Expand all phases by default
        const expanded = {};
        data.phases?.forEach(p => {
          expanded[p.phase_name] = true;
        });
        setExpandedPhases(expanded);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (scanId) {
      fetchTimeline();
    }
  }, [scanId]);

  // Start SSE replay
  const startReplay = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setCurrentEventIndex(-1);
    setIsPlaying(true);

    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const backendUrl = process.env.REACT_APP_API_URL || '';
    // EventSource doesn't support headers, pass token as query parameter
    const url = `${backendUrl}/api/v1/timeline/${scanId}/replay?speed=${speed}&token=${encodeURIComponent(token)}`;

    // Create EventSource with auth token in URL
    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'start') {
        console.log(`Replay started: ${data.total_events} events`);
      } else if (data.type === 'event') {
        setCurrentEventIndex(data.index);
        setSelectedEvent(data.event);

        // Scroll to event in timeline
        if (timelineRef.current) {
          const eventElement = timelineRef.current.querySelector(`[data-event-id="${data.event.id}"]`);
          if (eventElement) {
            eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      } else if (data.type === 'complete') {
        setIsPlaying(false);
        eventSource.close();
      }
    };

    eventSource.onerror = () => {
      setIsPlaying(false);
      eventSource.close();
    };
  }, [scanId, speed]);

  // Stop replay
  const stopReplay = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  // Reset replay
  const resetReplay = useCallback(() => {
    stopReplay();
    setCurrentEventIndex(-1);
    setSelectedEvent(null);
  }, [stopReplay]);

  // Toggle phase expansion
  const togglePhase = (phaseName) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseName]: !prev[phaseName]
    }));
  };

  // Filter events
  const filteredEvents = timeline?.events?.filter(event => {
    if (phaseFilter !== 'all' && event.phase !== phaseFilter) return false;
    if (severityFilter !== 'all' && event.severity !== severityFilter) return false;
    return true;
  }) || [];

  // Group events by phase
  const eventsByPhase = {};
  filteredEvents.forEach(event => {
    if (!eventsByPhase[event.phase]) {
      eventsByPhase[event.phase] = [];
    }
    eventsByPhase[event.phase].push(event);
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          <span className="ml-3 text-gray-400">Chargement de la timeline...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-red-500/50">
        <div className="flex items-center text-red-400">
          <XCircle className="w-5 h-5 mr-2" />
          <span>Erreur: {error}</span>
        </div>
      </div>
    );
  }

  if (!timeline || timeline.total_events === 0) {
    // Show Attack Path Visualization instead of empty state
    if (vulnerabilities && vulnerabilities.length > 0) {
      return (
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <AttackPathVisualization
            scanId={scanId}
            vulnerabilities={vulnerabilities}
            hosts={hosts}
          />
        </div>
      );
    }

    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
        <div className="flex flex-col items-center justify-center h-48 text-gray-400">
          <Activity className="w-12 h-12 mb-3 opacity-50" />
          <p>Aucun événement dans la timeline</p>
          <p className="text-sm mt-1">Les événements apparaîtront après le scan</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
      {/* Header with controls */}
      <div className="p-4 border-b border-slate-700/50 bg-slate-900/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">Attack Timeline</h3>
            <span className="text-sm text-gray-400">
              ({timeline.total_events} événements, {timeline.total_findings} findings)
            </span>
          </div>

          {/* Replay controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={resetReplay}
              className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 text-gray-400 hover:text-white transition-colors"
              title="Reset"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            <button
              onClick={isPlaying ? stopReplay : startReplay}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                isPlaying
                  ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
                  : 'bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400'
              }`}
            >
              {isPlaying ? (
                <>
                  <Pause className="w-4 h-4" />
                  <span>Pause</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  <span>Replay</span>
                </>
              )}
            </button>

            {/* Speed control */}
            <select
              value={speed}
              onChange={(e) => setSpeed(parseFloat(e.target.value))}
              className="bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
              disabled={isPlaying}
            >
              <option value="0.5">0.5x</option>
              <option value="1">1x</option>
              <option value="2">2x</option>
              <option value="5">5x</option>
              <option value="10">10x</option>
            </select>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={phaseFilter}
              onChange={(e) => setPhaseFilter(e.target.value)}
              className="bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white"
            >
              <option value="all">Toutes les phases</option>
              {Object.entries(PHASE_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>

          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white"
          >
            <option value="all">Toutes sévérités</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="info">Info</option>
          </select>

          {/* Progress indicator */}
          {isPlaying && (
            <div className="flex items-center gap-2 text-sm text-cyan-400">
              <div className="animate-pulse w-2 h-2 bg-cyan-400 rounded-full"></div>
              <span>Event {currentEventIndex + 1} / {timeline.total_events}</span>
            </div>
          )}
        </div>
      </div>

      {/* Timeline content */}
      <div ref={timelineRef} className="max-h-[600px] overflow-y-auto p-4 space-y-4">
        {/* Phases */}
        {timeline.phases?.map((phase) => {
          const config = PHASE_CONFIG[phase.phase_name] || PHASE_CONFIG.reconnaissance;
          const PhaseIcon = config.icon;
          const phaseEvents = eventsByPhase[phase.phase_name] || [];

          return (
            <div key={phase.id} className={`rounded-lg border ${config.borderColor} ${config.bgColor}`}>
              {/* Phase header */}
              <button
                onClick={() => togglePhase(phase.phase_name)}
                className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors rounded-t-lg"
              >
                <div className="flex items-center gap-3">
                  <PhaseIcon className={`w-5 h-5 ${config.color}`} />
                  <span className={`font-medium ${config.color}`}>{config.label}</span>
                  <span className="text-sm text-gray-400">
                    ({phaseEvents.length} événements)
                  </span>
                  {phase.status === 'completed' && (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  )}
                </div>
                {expandedPhases[phase.phase_name] ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {/* Phase events */}
              {expandedPhases[phase.phase_name] && phaseEvents.length > 0 && (
                <div className="p-3 pt-0 space-y-2">
                  {phaseEvents.map((event, idx) => {
                    const ToolIcon = TOOL_ICONS[event.tool] || Terminal;
                    const isCurrentEvent = selectedEvent?.id === event.id;
                    const severityStyle = event.severity ? SEVERITY_COLORS[event.severity] : '';

                    return (
                      <div
                        key={event.id}
                        data-event-id={event.id}
                        className={`p-3 rounded-lg border transition-all cursor-pointer ${
                          isCurrentEvent
                            ? 'bg-cyan-500/20 border-cyan-500 ring-2 ring-cyan-500/50'
                            : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
                        }`}
                        onClick={() => setSelectedEvent(event)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <ToolIcon className={`w-4 h-4 mt-0.5 ${config.color}`} />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-white">{typeof event.action === 'string' ? event.action : String(event.action || '')}</span>
                                {event.severity && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full border ${severityStyle}`}>
                                    {typeof event.severity === 'string' ? event.severity : String(event.severity || '')}
                                  </span>
                                )}
                              </div>
                              {event.target_ip && (
                                <div className="flex items-center gap-1 text-sm text-gray-400 mt-1">
                                  <Target className="w-3 h-3" />
                                  <span>{String(event.target_ip)}</span>
                                  {event.target_port && <span>:{String(event.target_port)}</span>}
                                  {event.service && <span className="text-cyan-400">({String(event.service)})</span>}
                                </div>
                              )}
                              {event.result && (
                                <p className="text-sm text-gray-300 mt-1">{typeof event.result === 'string' ? event.result : String(event.result || '')}</p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {event.tool && (
                              <span className="px-2 py-0.5 bg-slate-700 rounded">{event.tool}</span>
                            )}
                            {event.duration_ms && (
                              <span>{event.duration_ms}ms</span>
                            )}
                          </div>
                        </div>

                        {/* Event details (expanded) */}
                        {isCurrentEvent && event.details && Object.keys(event.details).length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-700/50">
                            <pre className="text-xs text-gray-400 bg-slate-900/50 p-2 rounded overflow-x-auto">
                              {JSON.stringify(event.details, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Attack Surfaces summary */}
        {timeline.attack_surfaces?.length > 0 && (
          <div className="mt-6 pt-6 border-t border-slate-700/50">
            <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
              <Server className="w-4 h-4" />
              Surfaces d'attaque découvertes
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {timeline.attack_surfaces.map((surface) => (
                <div
                  key={surface.id}
                  className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-white">{surface.target_ip}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      surface.risk_score >= 70 ? 'bg-red-500/20 text-red-400' :
                      surface.risk_score >= 40 ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      Risk: {surface.risk_score}
                    </span>
                  </div>
                  {surface.hostname && (
                    <p className="text-sm text-gray-400">{surface.hostname}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>{surface.open_ports?.length || 0} ports</span>
                    <span>{surface.vulnerabilities?.length || 0} vulns</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttackTimeline;
